(() => {
  'use strict';

  const ROOT = '[data-il-next]';
  if (window.IronLightNext && typeof window.IronLightNext.mount === 'function') {
    window.IronLightNext.mount(document);
    return;
  }

  const instances = new Map();
  const own = (root, selector) => Array.from(root.querySelectorAll(selector))
    .filter((element) => element.closest(ROOT) === root);
  const first = (root, selector) => own(root, selector)[0] || null;
  const mediaURL = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, document.baseURI);
      return /^(https?:)$/.test(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };
  const focus = (element) => {
    if (!element || !element.isConnected || typeof element.focus !== 'function') return;
    try { element.focus({ preventScroll: true }); } catch (_) { element.focus(); }
  };
  const label = (button, selector, text) => {
    if (!button) return;
    (button.querySelector(selector) || button).textContent = text;
    button.setAttribute('aria-label', text);
  };

  function campaign(root, listen, cleanup) {
    const films = own(root, 'video[data-il-film]');
    if (!films.length) return;
    const byTone = new Map(films.map((film) => [film.dataset.ilFilm, film]));
    const choices = own(root, '[data-il-tone]');
    const headlines = own(root, '[data-il-headline]');
    const pauseButton = first(root, '[data-il-pause]');
    const soundButton = first(root, '[data-il-sound]');
    const status = first(root, '[data-il-film-status]');
    const stage = films[0].closest('[data-il-film-stage]') || films[0].parentElement || root;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 760px)');
    const selected = choices.find((choice) => choice.getAttribute('aria-pressed') === 'true');
    let tone = selected && byTone.has(selected.dataset.ilTone)
      ? selected.dataset.ilTone : (byTone.has('black') ? 'black' : films[0].dataset.ilFilm);
    let inView = typeof window.IntersectionObserver !== 'function';
    let userPaused = false;
    let motionOverride = false;
    let soundOn = false;
    let playbackBlocked = false;
    let pageHidden = false;
    let playAttempt = null;
    let destroyed = false;

    const activeFilm = () => byTone.get(tone);
    const sourceFor = (film) => mediaURL(
      (mobile.matches && film.dataset.mobileSrc) || film.dataset.desktopSrc || film.getAttribute('src')
    );
    const mayPlay = () => !destroyed && inView && !document.hidden && !pageHidden
      && !userPaused && !playbackBlocked && (!motion.matches || motionOverride);
    const announce = (text) => { if (status) status.textContent = text; };

    function updateControls() {
      if (destroyed) return;
      const film = activeFilm();
      const playing = Boolean(film && !film.paused && !film.ended);
      const available = Boolean(film && sourceFor(film));
      if (pauseButton) {
        pauseButton.disabled = !available;
        pauseButton.removeAttribute('aria-pressed');
        label(pauseButton, '[data-il-pause-label]', playing
          ? (pauseButton.dataset.labelPause || 'Pause film')
          : (pauseButton.dataset.labelPlay || 'Play film'));
      }
      if (soundButton) {
        const allowed = tone === 'black';
        soundButton.hidden = !allowed;
        soundButton.disabled = !allowed || !available;
        soundButton.setAttribute('aria-hidden', String(!allowed));
        soundButton.setAttribute('aria-pressed', String(allowed && soundOn));
        label(soundButton, '[data-il-sound-label]', soundOn
          ? (soundButton.dataset.labelMute || 'Mute film')
          : (soundButton.dataset.labelUnmute || 'Hear the film'));
      }
      root.dataset.ilCurrentTone = tone;
      root.dataset.ilPaused = String(!playing);
    }

    function sync() {
      if (destroyed) return;
      const current = activeFilm();
      films.forEach((film) => {
        const active = film === current;
        film.hidden = !active;
        film.classList.toggle('is-active', active);
        film.setAttribute('aria-hidden', String(!active));
        film.muted = !active || tone !== 'black' || !soundOn;
        if (!active || !mayPlay()) film.pause();
      });
      choices.forEach((choice) => {
        choice.disabled = !byTone.has(choice.dataset.ilTone);
        choice.setAttribute('aria-pressed', String(choice.dataset.ilTone === tone));
      });
      headlines.forEach((headline) => {
        const active = headline.dataset.ilHeadline === tone;
        headline.hidden = !active;
        headline.setAttribute('aria-hidden', String(!active));
      });

      if (!current || !mayPlay()) {
        playAttempt = null;
        root.dataset.ilFilmState = current && current.getAttribute('src') ? 'paused' : 'poster';
        updateControls();
        return;
      }

      const source = sourceFor(current);
      if (!source) {
        root.dataset.ilFilmState = 'unavailable';
        updateControls();
        return;
      }
      if (!current.getAttribute('src')) {
        current.src = source;
        current.preload = 'none';
      }
      if (!current.paused || (playAttempt && playAttempt.film === current)) {
        updateControls();
        return;
      }

      const attempt = { film: current };
      playAttempt = attempt;
      root.dataset.ilFilmState = 'loading';
      const rejected = () => {
        if (destroyed || playAttempt !== attempt) return;
        playAttempt = null;
        playbackBlocked = true;
        soundOn = false;
        current.muted = true;
        root.dataset.ilFilmState = 'paused';
        announce(root.dataset.labelFilmBlocked || 'Select Play to watch the film.');
        updateControls();
      };
      try {
        const request = current.play();
        if (request && typeof request.then === 'function') {
          request.then(() => {
            if (destroyed || playAttempt !== attempt) return;
            playAttempt = null;
            if (!mayPlay() || activeFilm() !== current) current.pause();
            updateControls();
          }).catch(rejected);
        } else {
          playAttempt = null;
        }
      } catch (_) {
        rejected();
      }
      updateControls();
    }

    films.forEach((film) => {
      film.autoplay = false;
      film.defaultMuted = true;
      film.muted = true;
      film.playsInline = true;
      film.preload = 'none';
      film.pause();
      listen(film, 'playing', () => {
        if (film !== activeFilm() || !mayPlay()) {
          film.pause();
          return;
        }
        root.dataset.ilFilmState = 'playing';
        announce('');
        updateControls();
      });
      listen(film, 'pause', updateControls);
      listen(film, 'error', () => {
        if (destroyed || film !== activeFilm()) return;
        playAttempt = null;
        playbackBlocked = true;
        soundOn = false;
        film.muted = true;
        root.dataset.ilFilmState = 'unavailable';
        announce(root.dataset.labelFilmUnavailable
          || 'The film is unavailable. You can still explore the garment below.');
        updateControls();
      });
    });

    choices.forEach((choice) => listen(choice, 'click', () => {
      const next = choice.dataset.ilTone;
      if (!byTone.has(next) || next === tone) return;
      tone = next;
      soundOn = false;
      playbackBlocked = false;
      playAttempt = null;
      announce('');
      try { activeFilm().currentTime = 0; } catch (_) {}
      sync();
    }));

    if (pauseButton) listen(pauseButton, 'click', () => {
      const film = activeFilm();
      if (!film) return;
      if (!film.paused || (playAttempt && playAttempt.film === film)) {
        userPaused = true;
      } else {
        userPaused = false;
        motionOverride = true;
        playbackBlocked = false;
        announce('');
      }
      sync();
    });
    if (soundButton) listen(soundButton, 'click', () => {
      if (tone !== 'black') return;
      soundOn = !soundOn;
      if (soundOn) {
        userPaused = false;
        motionOverride = true;
        playbackBlocked = false;
        announce('');
      }
      sync();
    });

    const onMotionChange = () => {
      motionOverride = false;
      if (motion.matches) soundOn = false;
      sync();
    };
    if (typeof motion.addEventListener === 'function') {
      listen(motion, 'change', onMotionChange);
    } else if (typeof motion.addListener === 'function') {
      motion.addListener(onMotionChange);
      cleanup(() => motion.removeListener(onMotionChange));
    }
    listen(document, 'visibilitychange', () => {
      if (document.hidden) soundOn = false;
      sync();
    });
    listen(window, 'pagehide', () => { pageHidden = true; soundOn = false; sync(); });
    listen(window, 'pageshow', () => { pageHidden = false; sync(); });

    if (typeof window.IntersectionObserver === 'function') {
      const observer = new window.IntersectionObserver((entries) => {
        const entry = entries.find((item) => item.target === stage);
        if (!entry || destroyed) return;
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        if (visible === inView) return;
        inView = visible;
        if (!inView) soundOn = false;
        sync();
      }, { threshold: [0, 0.15] });
      observer.observe(stage);
      cleanup(() => observer.disconnect());
    }
    sync();
    cleanup(() => {
      destroyed = true;
      playAttempt = null;
      films.forEach((film) => { film.muted = true; film.pause(); });
    });
  }

  function gallery(root, listen, cleanup) {
    const dialog = first(root, 'dialog[data-il-gallery-dialog]');
    const image = dialog && dialog.querySelector('[data-il-view-image]');
    if (!dialog || !image || typeof dialog.showModal !== 'function') return;
    const caption = dialog.querySelector('[data-il-view-label]');
    const closeButton = dialog.querySelector('[data-il-view-close]');
    const previous = dialog.querySelector('[data-il-view-prev]');
    const next = dialog.querySelector('[data-il-view-next]');
    let views = [];
    let index = 0;
    let returnFocus = null;
    let currentCaption = '';

    function show(position) {
      if (!views.length) return false;
      index = (position + views.length) % views.length;
      const view = views[index];
      const source = mediaURL(view.dataset.ilViewSrc);
      if (!source) return false;
      image.alt = view.dataset.ilViewAlt || dialog.dataset.labelViewAlt
        || 'IRON LIGHT garment development view';
      image.src = source;
      const viewLabel = view.dataset.ilViewLabel || dialog.dataset.labelView || 'Garment view';
      currentCaption = `${viewLabel} / ${index + 1} ${dialog.dataset.labelOf || 'of'} ${views.length}`;
      if (caption) caption.textContent = currentCaption;
      if (previous) previous.hidden = views.length < 2;
      if (next) next.hidden = views.length < 2;
      return true;
    }

    listen(root, 'click', (event) => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey
        || event.shiftKey || event.altKey) return;
      const view = event.target.closest && event.target.closest('[data-il-view-src]');
      if (!view || view.closest(ROOT) !== root || !mediaURL(view.dataset.ilViewSrc)) return;
      const group = view.dataset.ilViewGroup || '';
      views = own(root, '[data-il-view-src]').filter((item) =>
        (item.dataset.ilViewGroup || '') === group && mediaURL(item.dataset.ilViewSrc));
      if (!show(views.indexOf(view))) return;
      try {
        if (!dialog.open) dialog.showModal();
      } catch (_) {
        return;
      }
      event.preventDefault();
      returnFocus = view;
      focus(closeButton);
    });
    if (closeButton) listen(closeButton, 'click', () => dialog.close());
    if (previous) listen(previous, 'click', () => show(index - 1));
    if (next) listen(next, 'click', () => show(index + 1));
    listen(dialog, 'keydown', (event) => {
      if (!dialog.open || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        show(index + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    });
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right
        || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    listen(dialog, 'close', () => { focus(returnFocus); returnFocus = null; });
    listen(image, 'error', () => {
      if (caption) caption.textContent = `${currentCaption} — ${dialog.dataset.labelImageError
        || 'Image could not be loaded.'}`;
    });
    cleanup(() => {
      returnFocus = null;
      if (dialog.open) dialog.close();
    });
  }

  function destroy(root) {
    const dispose = instances.get(root);
    if (!dispose) return;
    dispose();
    instances.delete(root);
  }
  function mount(scope = document) {
    instances.forEach((_, root) => { if (!root.isConnected) destroy(root); });
    const roots = [];
    if (scope.matches && scope.matches(ROOT)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const disposals = [];
      const cleanup = (dispose) => disposals.push(dispose);
      const listen = (target, event, listener, options) => {
        target.addEventListener(event, listener, options);
        cleanup(() => target.removeEventListener(event, listener, options));
      };
      instances.set(root, () => disposals.reverse().forEach((dispose) => dispose()));
      campaign(root, listen, cleanup);
      gallery(root, listen, cleanup);
    });
  }
  function destroyWithin(scope) {
    instances.forEach((_, root) => {
      if (root === scope || (scope.contains && scope.contains(root))) destroy(root);
    });
  }

  window.IronLightNext = { mount, destroyWithin };
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:section:load', (event) => {
    const parent = event.target.closest && event.target.closest(ROOT);
    if (parent) { destroyWithin(parent); mount(parent); } else { mount(event.target); }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  } else {
    mount(document);
  }

})();
